/**
 * Dokumente -- der Arbeitsbereich, an dem bis heute ein Satz stand.
 *
 * FUENF FAECHER, IMMER ALLE FUENF.
 *
 * Lichtplanung, Produktdatenblatt, Treiberdatenblatt, Zertifikate,
 * Anleitung. Sie stehen da, ob etwas darin liegt oder nicht. Ein leeres Fach
 * sagt „nicht vorhanden" -- und das ist eine Auskunft. Nur die vorhandenen
 * Faecher zu zeigen hiesse, dem Leser die Frage zu ueberlassen, ob es hier
 * nie eine Anleitung gab oder ob sie nur fehlt.
 *
 * DIE LESEANSICHT ZEIGT KEINE BEDIENUNG.
 *
 * Ohne Bearbeitungsmodus steht je Datei nur, was man wissen will: Name,
 * Sprache und Stand, sofern gepflegt, und zwei Wege hinein -- Oeffnen und
 * Herunterladen. Kein Hochladefeld, kein Papierkorb, keine Schaltflaeche,
 * die nichts tut. Dieselbe Seite soll auf einem Tablet vor einem Kunden
 * stehen koennen.
 *
 * Bearbeitet wird ueber den EINEN Stift in der Bereichsleiste -- dasselbe
 * Muster wie Technik, Logistik und Einkauf (`ArticleDetail.tsx`). Erst dort
 * erscheinen Hochladen, Ersetzen und Entfernen.
 *
 * GEERBT WIRD SICHTBAR.
 *
 * Ein Dokument kann am PRODUKTMODELL haengen -- Datenblatt, Anleitung,
 * Zertifikate gelten fuer die ganze Baureihe -- oder an der AUSFUEHRUNG, wie
 * die IES-Datei, die deren Lichtstrom beschreibt. Diese Ansicht zeigt beides
 * nebeneinander und schreibt an jedes Modelldokument, dass es geerbt ist.
 *
 * Das ist nicht Zierde. Wer hier eine Anleitung ersetzt, ohne zu wissen, dass
 * sie am Modell haengt, tauscht sie fuer alle vier Ausfuehrungen aus. Deshalb
 * traegt auch jede Schaltflaeche an einem geerbten Dokument eine andere
 * Beschriftung: „am Modell ersetzen", nicht „ersetzen".
 *
 * WAS HIER NICHT ENTSTEHT
 *
 * Kein Dokumentenmanagement. Keine Ordner, keine Suche, keine Freigabekette,
 * keine Ablauferinnerung. Fuenf Faecher und eine Datei je Eintrag -- mehr
 * verlangt der Vorgang nicht, und mehr zu bauen hiesse, eine zweite
 * Anwendung in die Produktakte zu stellen.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DOCUMENT_FILE_TYPES, MAX_DOCUMENT_BYTES,
  type ArticleCapability, type ArticleDocumentsResponse, type ProductDocumentGroupCode,
  type DocumentLevel, type ProductDocument, type TextLanguageCode,
} from "@homebase/shared";
import { api, ApiError } from "../lib/api.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import "./ArticleDocuments.css";

/**
 * Die Steuerung, die der Rahmen braucht -- dieselbe Form wie `TechnikSteuerung`.
 *
 * Offen ist hier genau eine Sache: eine gewaehlte Datei, die noch nicht
 * hochgeladen wurde. Das sieht harmlos aus und ist es nicht -- wer eine Datei
 * aussucht, ein Datum eintraegt und dann den Reiter wechselt, hat die Arbeit
 * verloren und merkt es erst spaeter.
 */
export interface DokumenteSteuerung {
  readonly dirty: boolean;
  /** Laedt die offene Datei hoch. `true`, wenn danach nichts mehr offen ist. */
  readonly save: () => Promise<boolean>;
  readonly discard: () => void;
}

/**
 * Die Beschriftung der fuenf Faecher.
 *
 * Woertlich zugeordnet und nicht ueber `tCode` aus dem Gruppencode gebildet.
 * Der Grund ist die Pruefung `checki18n`: sie erkennt einen Schluessel als
 * benutzt, wenn er im Code steht. Ein aus einem Code zusammengesetzter
 * Schluessel braucht dort einen Eintrag in einer Ausnahmeliste -- eine
 * Ausnahme, die man fuer fuenf feste Werte nicht braucht.
 */
const FACH_TITEL: Record<ProductDocumentGroupCode, Parameters<I18n["t"]>[0]> = {
  lichtplanung_ies: "doc.groupLichtplanung",
  produktdatenblatt: "doc.groupDatenblatt",
  led_treiber_datenblatt: "doc.groupTreiber",
  zertifikate_konformitaet: "doc.groupZertifikate",
  produktanleitung: "doc.groupAnleitung",
  pruefberichte_sonstiges: "doc.groupPruefberichte",
};

/*
 * WIE VIELE DATEIEN EIN FACH OHNE KLICK ZEIGT.
 *
 * Ein Fach mit zwanzig Messberichten verlaengerte die Seite, bis das naechste
 * Fach unter dem Bildrand verschwand -- wer ein Datenblatt suchte, scrollte
 * an Pruefprotokollen vorbei. Vier genuegen, um zu sehen, was in einem Fach
 * liegt; der Rest steht einen Klick entfernt.
 *
 * Im Bearbeitungsmodus steht alles offen: wer eine Datei ersetzen will, muss
 * sie finden koennen, ohne erst aufzuklappen.
 */
const VORSCHAU = 4;

/**
 * Was der Dateiauswahldialog anbieten soll.
 *
 * Aus derselben Liste gebildet, aus der der Server den Inhaltstyp ableitet.
 * Eine zweite, hier von Hand gepflegte Aufzaehlung waere nach der ersten
 * Erweiterung falsch -- und zwar auf die unangenehme Art: der Dialog boete
 * eine Datei nicht an, die der Server annaehme.
 */
const ACCEPT = Object.keys(DOCUMENT_FILE_TYPES).map((e) => `.${e}`).join(",");

/** Die offene Eingabe -- hoechstens eine zur Zeit. */
interface Entwurf {
  readonly gruppe: ProductDocumentGroupCode;
  /** Der Stand, der abgeloest wird. `null`: eine neue Datei. */
  readonly ersetzt: ProductDocument | null;
  /** Nur bei einer neuen Datei waehlbar. */
  readonly ebene: DocumentLevel;
  readonly datei: File | null;
  readonly sprache: TextLanguageCode | "";
  readonly stand: string;
}

export function ArticleDocuments({
  articleId, modelId, mayWrite, editing, onEditingChange, steuerung,
}: {
  articleId: number;
  /**
   * AM PRODUKTMODELL statt an einer SKU (Produktmodelle 002).
   *
   * Dieselbe Ansicht, dieselben fuenf Faecher, dieselbe Bedienung -- nur
   * gelesen und abgelegt wird am Modell. Eine Ebenenwahl entfaellt: was hier
   * hochgeladen wird, gilt fuer alle Ausfuehrungen. `articleId` wird dann
   * nicht benutzt.
   */
  modelId?: number;
  /** Die Oberflaeche richtet sich danach; entschieden wird im Server. */
  mayWrite: boolean;
  editing: boolean;
  onEditingChange: (v: boolean) => void;
  /** Wird mit der aktuellen Steuerung belegt -- siehe `DokumenteSteuerung`. */
  steuerung: React.MutableRefObject<DokumenteSteuerung | null>;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [daten, setDaten] = useState<ArticleDocumentsResponse | null>(null);
  const [nachweise, setNachweise] = useState<readonly ArticleCapability[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Die Zeile, fuer die gerade nachgefragt wird, ob sie wirklich weg soll. */
  const [fragt, setFragt] = useState<number | null>(null);
  /* Aufgeklappte Faecher -- je Fach, nicht fuer die ganze Seite. */
  const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<ProductDocumentGroupCode>>(new Set());

  /* Die Modellansicht in der Form der Artikelansicht -- ohne Modellnamen,
     damit keine Ebenenwahl erscheint. */
  const holen = useCallback((): Promise<ArticleDocumentsResponse> => modelId !== undefined
    ? api.modelDocuments(modelId).then((r) => ({ articleId: 0, modelId, modelName: null, groups: r.groups }))
    : api.articleDocuments(articleId), [articleId, modelId]);

  const load = useCallback(() => {
    setError(null);
    holen().then(setDaten).catch(setError);
    if (modelId !== undefined) { setNachweise([]); return; }
    /*
     * Die nachgewiesenen Eigenschaften kommen aus ihrem eigenen Endpunkt und
     * nicht aus der Dokumentantwort. Sie sind keine Datei -- sie sind die
     * AUSSAGE, zu der eine Urkunde gehoert. Beides in einen Vertrag zu
     * ziehen hiesse, den Nachweis von der Existenz eines PDF abhaengig zu
     * machen.
     *
     * Ein Fehler hier bleibt folgenlos: die Dokumente stehen auch ohne den
     * Nachweis, und ein leerer Bereich waere die schlechtere Antwort auf
     * eine Nebenauskunft.
     */
    api.articleCapabilities(articleId)
      .then((r) => setNachweise(r.items)).catch(() => setNachweise([]));
  }, [articleId, modelId, holen]);

  useEffect(() => { setDaten(null); setEntwurf(null); setFragt(null); load(); }, [load]);

  /* Der Bearbeitungsmodus schliesst -- dann ist auch keine Eingabe mehr offen. */
  useEffect(() => { if (!editing) { setEntwurf(null); setFragt(null); } }, [editing]);

  const bearbeitet = editing && mayWrite;

  /**
   * Die offene Datei ablegen.
   *
   * Der Inhalt geht als Base64 hinaus -- der Vertrag nennt den Grund. Gelesen
   * wird er ueber `FileReader`, weil dessen Ergebnis bereits kodiert ist;
   * selbst zu kodieren hiesse, ein 20-MB-Feld in Stuecken durch `btoa` zu
   * schieben und dabei den einen Fehler zu machen, den man dort macht.
   */
  const hochladen = useCallback(async (): Promise<boolean> => {
    if (!entwurf?.datei) return true;
    const datei = entwurf.datei;
    if (datei.size > MAX_DOCUMENT_BYTES) {
      /*
       * Die Groesse wird HIER schon geprueft, obwohl der Server sie ohnehin
       * prueft. Sonst wanderten 30 MB durchs Netz, um dann abgewiesen zu
       * werden -- an einem Mobilfunkanschluss dauert das eine Minute, die
       * niemand braucht.
       *
       * Abgewiesen wird mit DEMSELBEN Fehlercode, den der Server schicken
       * wuerde. Ein eigener Text an dieser Stelle waere ein zweiter
       * Textbestand fuer denselben Sachverhalt.
       */
      setError(new ApiError(413, "document_too_large", "document_too_large"));
      return false;
    }
    setLaeuft(true);
    setError(null);
    try {
      const base64 = await new Promise<string>((ok, fehler) => {
        const leser = new FileReader();
        leser.onerror = () => fehler(leser.error);
        /* `readAsDataURL` liefert „data:<typ>;base64,<inhalt>". Der Vertrag
           will nur den Inhalt -- die Praeambel traegt den Typ des Browsers,
           und genau den nimmt der Server ausdruecklich nicht an. */
        leser.onload = () => ok(String(leser.result).split(",")[1] ?? "");
        leser.readAsDataURL(datei);
      });
      const eingabe = {
        group: entwurf.gruppe,
        level: modelId !== undefined ? "model" as const : entwurf.ebene,
        fileName: datei.name,
        contentBase64: base64,
        language: entwurf.sprache === "" ? null : entwurf.sprache,
        documentDate: entwurf.stand === "" ? null : entwurf.stand,
        replacesId: entwurf.ersetzt?.id ?? null,
      };
      if (modelId !== undefined) {
        const r = await api.uploadModelDocument(modelId, eingabe);
        setDaten({ articleId: 0, modelId, modelName: null, groups: r.groups });
      } else {
        setDaten(await api.uploadArticleDocument(articleId, eingabe));
      }
      setEntwurf(null);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setLaeuft(false);
    }
  }, [articleId, modelId, entwurf]);

  const entfernen = useCallback(async (id: number): Promise<void> => {
    setLaeuft(true);
    setError(null);
    try {
      await api.removeDocument(id);
      setFragt(null);
      setDaten(await holen());
    } catch (e) { setError(e); }
    finally { setLaeuft(false); }
  }, [holen]);

  /* Der Rahmen fragt ueber diese Kennung nach offenen Eingaben. */
  const hochladenRef = useRef(hochladen);
  hochladenRef.current = hochladen;
  const offen = entwurf?.datei != null;
  useEffect(() => {
    steuerung.current = {
      dirty: offen,
      save: () => hochladenRef.current(),
      discard: () => setEntwurf(null),
    };
    return () => { steuerung.current = null; };
  }, [offen, steuerung]);

  if (error !== null && !daten) {
    return <div className="doc"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!daten) return <div className="doc"><LoadingState label={t("doc.loading")} /></div>;

  return (
    <section className={bearbeitet ? "doc doc--edit" : "doc"} aria-label={t("doc.section")}>
      {/*
        * DER MODUS IST BESCHRIFTET -- wie in der Technik.
        *
        * Ein aktiver Stift in der Leiste ist zwoelf mal zwoelf Pixel. Wer
        * eine Datei sucht, sieht die Leiste nicht; er muss hier ablesen
        * koennen, dass er im Bearbeitungsmodus ist und wie er herauskommt.
        */}
      {bearbeitet && (
        <div className="doc__modus" role="status">
          <span className="doc__moduslabel">{t("doc.editing")}</span>
          <span className="doc__modushint">{t("doc.editHint")}</span>
          <button className="btn btn--small doc__done" disabled={laeuft}
            onClick={() => { setEntwurf(null); onEditingChange(false); }}>
            {offen ? t("common.cancel") : t("doc.done")}
          </button>
        </div>
      )}

      {error !== null && (
        <p className="doc__fehler" role="alert">{errorText(i18n, error)}</p>
      )}

      <div className="doc__grid">
        {daten.groups.map((g) => {
          const belegt = g.documents.length > 0;
          /*
           * DER BALLWURFSICHERHEITSNACHWEIS STEHT BEI DEN ZERTIFIKATEN.
           *
           * Er ist keine Datei, sondern eine belegte Aussage ueber das
           * Produkt -- „ballwurfsicher nach DIN 18032-3". Fachlich gehoert
           * er genau dorthin, wo jemand nach CE, ENEC und der Urkunde
           * sucht, und nicht in einen eigenen Winkel der Technikansicht.
           *
           * Er ersetzt die Urkunde NICHT. Steht der Nachweis da und fehlt
           * die Datei, ist das genau die Auskunft, die der Vertrieb
           * braucht: die Eigenschaft ist belegt, das Papier dazu fehlt noch.
           */
          const zeigtNachweis = g.group === "zertifikate_konformitaet"
            && nachweise.length > 0;
          return (
            <section className="doc__group" key={g.group}>
              <h3 className="doc__grouptitle">{t(FACH_TITEL[g.group])}</h3>

              {zeigtNachweis && (
                <ul className="doc__proven">
                  {nachweise.map((n) => (
                    <li key={n.capability} className="doc__provenrow">
                      <span className="doc__provenlabel">{t("doc.proven")}</span>
                      <span className="doc__provenname">
                        {i18n.tCode("capability", n.capability, n.capability)}
                      </span>
                      <span className="doc__provennorms">
                        {n.evidence.map((e) => e.standard.code).join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {belegt ? (<>
                <ul className="doc__list">
                  {(bearbeitet || aufgeklappt.has(g.group) ? g.documents : g.documents.slice(0, VORSCHAU)).map((d) => (
                    <DokumentZeile key={d.id} d={d} i18n={i18n}
                      bearbeitet={bearbeitet} laeuft={laeuft}
                      fragt={fragt === d.id}
                      onFragen={() => setFragt(fragt === d.id ? null : d.id)}
                      onEntfernen={() => void entfernen(d.id)}
                      onErsetzen={() => setEntwurf({
                        gruppe: g.group, ersetzt: d,
                        ebene: d.level, datei: null,
                        sprache: d.language ?? "", stand: d.documentDate ?? "",
                      })} />
                  ))}
                </ul>
                {!bearbeitet && g.documents.length > VORSCHAU && (
                  <button type="button" className="doc__mehr"
                    aria-expanded={aufgeklappt.has(g.group)}
                    onClick={() => setAufgeklappt((alt) => {
                      const neu = new Set(alt);
                      if (neu.has(g.group)) neu.delete(g.group); else neu.add(g.group);
                      return neu;
                    })}>
                    {aufgeklappt.has(g.group)
                      ? t("doc.less")
                      : t("doc.more", { n: g.documents.length - VORSCHAU })}
                  </button>
                )}
              </>) : (
                /* Der ruhige Leerzustand. Kein Ausrufezeichen, keine Farbe --
                   ein fehlendes Treiberdatenblatt ist kein Fehler, sondern
                   eine Tatsache. */
                !zeigtNachweis && <p className="doc__empty">{t("doc.none")}</p>
              )}

              {bearbeitet && entwurf?.gruppe !== g.group && (
                <button type="button" className="btn btn--small doc__add"
                  disabled={laeuft}
                  onClick={() => setEntwurf({
                    gruppe: g.group, ersetzt: null, ebene: "article",
                    datei: null, sprache: "", stand: "",
                  })}>{t("doc.add")}</button>
              )}

              {bearbeitet && entwurf?.gruppe === g.group && (
                <Formular entwurf={entwurf} i18n={i18n} laeuft={laeuft}
                  modelName={daten.modelName}
                  onAendern={setEntwurf}
                  onAbbrechen={() => setEntwurf(null)}
                  onHochladen={() => void hochladen()} />
              )}
            </section>
          );
        })}
      </div>
    </section>
  );
}

/** Eine Datei -- in der Leseansicht und im Bearbeitungsmodus dieselbe Zeile. */
function DokumentZeile({ d, i18n, bearbeitet, laeuft, fragt, onFragen, onEntfernen, onErsetzen }: {
  d: ProductDocument;
  i18n: I18n;
  bearbeitet: boolean;
  laeuft: boolean;
  fragt: boolean;
  onFragen: () => void;
  onEntfernen: () => void;
  onErsetzen: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <li className="doc__item">
      <div className="doc__head">
        <span className="doc__name" title={d.fileName}>{d.fileName}</span>
        {d.inherited && (
          /* Die Kennzeichnung steht am Dateinamen und nicht in einer
             Fusszeile: sie aendert, was ein Klick auf „Ersetzen" bedeutet,
             und muss deshalb dort stehen, wo man liest, bevor man klickt. */
          <span className="doc__badge"
            title={d.inheritedFrom
              ? t("doc.inheritedFrom", { model: d.inheritedFrom.name })
              : t("doc.inherited")}>{t("doc.inherited")}</span>
        )}
      </div>

      {/*
        * Sprache und Stand stehen nur da, WENN sie gepflegt sind.
        *
        * Ein „Sprache: —" fuellte die Zeile mit der Auskunft, dass es keine
        * Auskunft gibt. Die Groesse steht immer -- sie sagt vor dem Klick,
        * ob man auf ein Mobilfunknetz wartet.
        */}
      <div className="doc__meta">
        {d.language && (
          <span className="doc__metaitem">{i18n.tCode("textLang", d.language, d.language)}</span>
        )}
        {d.documentDate && (
          <span className="doc__metaitem">
            {t("doc.dateLabel", { date: datumAnzeige(d.documentDate, i18n) })}
          </span>
        )}
        <span className="doc__metaitem doc__size">{groesse(d.byteSize, i18n)}</span>
      </div>

      <div className="doc__actions">
        {/*
          * Zwei Wege in dieselbe Datei, und sie sind nicht dasselbe.
          *
          * „Oeffnen" ist eine BITTE um Anzeige -- der Server gewaehrt sie
          * nur fuer Inhalte, die ein Browser anzeigen und nicht ausfuehren
          * kann, und schickt alles andere als Download. „Herunterladen"
          * fragt gar nicht erst.
          */}
        <a className="doc__link" href={api.documentUrl(d.id, true)}
          target="_blank" rel="noreferrer">{t("doc.open")}</a>
        <a className="doc__link" href={api.documentUrl(d.id, false)}
          download={d.fileName}>{t("doc.download")}</a>

        {bearbeitet && !fragt && (
          <>
            <button type="button" className="btn btn--small" disabled={laeuft}
              onClick={onErsetzen}>
              {d.inherited ? t("doc.replaceInherited") : t("doc.replace")}
            </button>
            <button type="button" className="btn btn--small" disabled={laeuft}
              onClick={onFragen}>
              {d.inherited ? t("doc.removeInherited") : t("doc.remove")}
            </button>
          </>
        )}
        {bearbeitet && fragt && (
          /*
           * DIE RUECKFRAGE STEHT IN DER ZEILE, NICHT IN EINEM DIALOG.
           *
           * Entfernen ist aus der Oberflaeche heraus nicht umkehrbar -- der
           * Stand bleibt zwar in der Datenbank, aber es gibt hier keinen Weg
           * zurueck. Ein Dialog ueber der Seite verdeckte dabei genau die
           * Zeile, um die es geht; hier steht die Frage neben dem Namen.
           */
          <>
            <span className="doc__ask">
              {d.inherited ? t("doc.removeAskModel") : t("doc.removeAsk")}
            </span>
            <button type="button" className="btn btn--small doc__danger" disabled={laeuft}
              onClick={onEntfernen}>{t("doc.removeYes")}</button>
            <button type="button" className="btn btn--small" disabled={laeuft}
              onClick={onFragen}>{t("common.cancel")}</button>
          </>
        )}
      </div>

      {d.previous.length > 0 && (
        /*
         * DIE VORGAENGERSTAENDE -- zugeklappt, aber vorhanden.
         *
         * Ein ersetztes Datenblatt verschwindet nicht. Es steht hier, mit
         * dem Tag seiner Abloesung, und laesst sich weiterhin herunterladen
         * -- genau dafuer wird es aufbewahrt. Aufgeklappt waere es
         * Rauschen: im Regelfall will man den geltenden Stand.
         */
        <details className="doc__history">
          <summary>{t("doc.previous", { n: d.previous.length })}</summary>
          <ul className="doc__historylist">
            {d.previous.map((v) => (
              <li key={v.id} className="doc__historyitem">
                <span className="doc__name">{v.fileName}</span>
                <span className="doc__metaitem">
                  {t("doc.replacedOn", { date: datumAnzeige(v.supersededAt, i18n) })}
                </span>
                <a className="doc__link" href={api.documentUrl(v.id, false)}
                  download={v.fileName}>{t("doc.download")}</a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

/**
 * Das Hochladeformular -- eines je Bereich, nie zwei zugleich.
 *
 * Zwei offene Formulare waeren zwei ungespeicherte Eingaben, und die
 * Rueckfrage des Rahmens („darf ich hier weg?") muesste beide kennen. Eines
 * zur Zeit macht aus „ist etwas offen" eine Frage mit einer Antwort.
 */
function Formular({ entwurf, i18n, laeuft, modelName, onAendern, onAbbrechen, onHochladen }: {
  entwurf: Entwurf;
  i18n: I18n;
  laeuft: boolean;
  /** Der Name des Produktmodells. `null`: die SKU gehoert zu keinem. */
  modelName: string | null;
  onAendern: (e: Entwurf) => void;
  onAbbrechen: () => void;
  onHochladen: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const ersetzt = entwurf.ersetzt;
  return (
    <div className="doc__form">
      <p className="doc__formtitle">
        {ersetzt ? t("doc.replacing", { name: ersetzt.fileName }) : t("doc.adding")}
      </p>

      <label className="doc__field">
        <span className="doc__fieldlabel">{t("doc.file")}</span>
        <input type="file" accept={ACCEPT} disabled={laeuft}
          onChange={(e) => onAendern({ ...entwurf, datei: e.target.files?.[0] ?? null })} />
      </label>
      <p className="doc__hint">{t("doc.formats", { mb: MAX_DOCUMENT_BYTES / 1024 / 1024 })}</p>

      {/*
        * DIE EBENE IST EINE ENTSCHEIDUNG, KEIN VORGABEWERT MIT AUSNAHME.
        *
        * Sie erscheint nur bei einer NEUEN Datei. Beim Ersetzen bleibt sie,
        * wo der Vorgaenger hing -- sonst wuerde aus dem Austausch einer
        * Modellanleitung stillschweigend eine Ausfuehrungsanleitung, und die
        * drei Geschwister verloeren ihre.
        *
        * Ohne Produktmodell gibt es nichts zu entscheiden: die Auswahl
        * erscheint dann gar nicht, statt eine Moeglichkeit anzubieten, die
        * der Server abweisen muesste.
        */}
      {!ersetzt && modelName !== null && (
        <fieldset className="doc__level">
          <legend className="doc__fieldlabel">{t("doc.level")}</legend>
          <label className="doc__radio">
            <input type="radio" name="doc-level" checked={entwurf.ebene === "article"}
              disabled={laeuft}
              onChange={() => onAendern({ ...entwurf, ebene: "article" })} />
            <span>{t("doc.levelArticle")}</span>
          </label>
          <label className="doc__radio">
            <input type="radio" name="doc-level" checked={entwurf.ebene === "model"}
              disabled={laeuft}
              onChange={() => onAendern({ ...entwurf, ebene: "model" })} />
            <span>{t("doc.levelModel", { model: modelName })}</span>
          </label>
        </fieldset>
      )}

      <div className="doc__fieldrow">
        <label className="doc__field">
          <span className="doc__fieldlabel">{t("doc.languageLabel")}</span>
          <select value={entwurf.sprache} disabled={laeuft}
            onChange={(e) => onAendern({
              ...entwurf, sprache: e.target.value as TextLanguageCode | "",
            })}>
            {/* „Keine Angabe" ist die Vorgabe und bleibt eine gueltige
                Antwort: eine IES-Datei hat keine Sprache, und eine erfundene
                waere schlechter als keine. */}
            <option value="">{t("doc.languageNone")}</option>
            <option value="de">{i18n.tCode("textLang", "de", "de")}</option>
            <option value="en">{i18n.tCode("textLang", "en", "en")}</option>
          </select>
        </label>

        <label className="doc__field">
          <span className="doc__fieldlabel">{t("doc.standLabel")}</span>
          <input type="date" value={entwurf.stand} disabled={laeuft}
            onChange={(e) => onAendern({ ...entwurf, stand: e.target.value })} />
        </label>
      </div>

      <div className="doc__formactions">
        <button type="button" className="btn btn--primary btn--small"
          disabled={laeuft || entwurf.datei === null} onClick={onHochladen}>
          {laeuft ? t("doc.uploading") : ersetzt ? t("doc.replace") : t("doc.upload")}
        </button>
        <button type="button" className="btn btn--small" disabled={laeuft}
          onClick={onAbbrechen}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}

/**
 * Ein Datum in der Sprache des Lesers.
 *
 * Nimmt ein reines Datum (`2026-03-01`) ebenso wie einen Zeitpunkt. Beim
 * reinen Datum wird die Zeitzone ausdruecklich umgangen: `new Date("2026-03-01")`
 * liest UTC-Mitternacht, und westlich von Greenwich waere das der 29.02.
 */
function datumAnzeige(iso: string, i18n: I18n): string {
  const nurDatum = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = nurDatum
    ? new Date(Number(nurDatum[1]), Number(nurDatum[2]) - 1, Number(nurDatum[3]))
    : new Date(iso);
  return d.toLocaleDateString(i18n.lang === "en" ? "en-GB" : "de-DE");
}

/**
 * Die Dateigroesse, gerundet.
 *
 * Unter einem Megabyte in Kilobyte, darueber mit einer Nachkommastelle. Die
 * exakte Byte-Zahl waere genauer und nutzloser -- gefragt ist „ist das gross",
 * nicht „wie gross genau".
 */
function groesse(bytes: number, i18n: I18n): string {
  const lokal = i18n.lang === "en" ? "en-GB" : "de-DE";
  if (bytes < 1024 * 1024) {
    return i18n.t("doc.sizeKb", { n: Math.max(1, Math.round(bytes / 1024)).toLocaleString(lokal) });
  }
  return i18n.t("doc.sizeMb", {
    n: (bytes / 1024 / 1024).toLocaleString(lokal, { maximumFractionDigits: 1 }),
  });
}
